import { CreateTaskRequest, TaskResponse, BaseTaskOutput } from './types';
import { Logger, ConsoleLogger } from './logger';

export class PIAPI {
    protected apiKey: string;
    protected baseUrl: string;
    protected logger: Logger;

    constructor(apiKey: string, baseUrl = 'https://api.piapi.ai/api/v1') {
        this.apiKey = apiKey;
        this.baseUrl = baseUrl;
        // 默认使用 ConsoleLogger，不启用调试日志
        this.logger = new ConsoleLogger(false);
    }

    // 添加启用/关闭调试日志的方法
    enableDebug(enable: boolean = true): void {
        this.logger = new ConsoleLogger(enable);
    }

    protected async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        this.logger.debug(`Making request to ${endpoint}`, options);
        
        const response = await fetch(`${this.baseUrl}${endpoint}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': this.apiKey,
                ...options.headers,
            },
        });

        const data = await response.json();
        this.logger.debug(`Response received:`, data);
        
        if (!response.ok) {
            // Log the error but don't throw, let the child class decide how to handle it
            this.logger.error(`X-API Error: ${response.statusText}`, data);
        }

        return data;
    }

    protected async createTask<T extends BaseTaskOutput>(request: CreateTaskRequest): Promise<TaskResponse<T>> {
        this.logger.info(`Creating new task`, { type: request.task_type, model: request.model });
        this.logger.debug('Request payload:', {
            task_type: request.task_type,
            model: request.model,
            input: request.input,
            config: request.config
        });
        
        return this.request<TaskResponse<T>>('/task', {
            method: 'POST',
            body: JSON.stringify(request),
        });
    }

    async getTask<T extends BaseTaskOutput>(taskId: string): Promise<TaskResponse<T>> {
        this.logger.debug(`Fetching task status`, { taskId });
        return this.request<TaskResponse<T>>(`/task/${taskId}`);
    }

    protected async waitForTask<T extends BaseTaskOutput>(
        taskId: string, 
        options: {
            maxAttempts?: number;
            interval?: number;
        } = {}
    ): Promise<TaskResponse<T>> {
        const { maxAttempts = 60, interval = 2000 } = options;
        let attempts = 0;

        this.logger.info(`Starting to poll task`, { taskId, maxAttempts, interval });

        while (attempts < maxAttempts) {
            try {
                const response = await this.getTask<T>(taskId);
                
                if (response.data.status === 'completed') {
                    this.logger.info(`Task completed successfully`, { taskId });
                    return response;
                }
                
                if (response.data.status === 'failed') {
                    const errorMessage = `Task failed: ${response.data.error?.message || 'Unknown error'}`;
                    this.logger.error(errorMessage, { taskId, error: response.data.error });
                    throw new Error(errorMessage);
                }
                
                this.logger.debug(`Task still processing`, { taskId, attempt: attempts + 1, status: response.data.status });
                await new Promise(resolve => setTimeout(resolve, interval));
                attempts++;
            } catch (error) {
                this.logger.error(`Error polling task`, { taskId, error });
                if (error instanceof Error) {
                    throw new Error(`Failed to poll task: ${error.message}`);
                }
                throw error;
            }
        }

        const timeoutError = `Task timeout after ${maxAttempts} attempts`;
        this.logger.error(timeoutError, { taskId });
        throw new Error(timeoutError);
    }

    protected async createTaskAndWait<T extends BaseTaskOutput>(
        request: CreateTaskRequest,
        options?: {
            maxAttempts?: number;
            interval?: number;
        }
    ): Promise<TaskResponse<T>> {
        const response = await this.createTask<T>(request);
        return this.waitForTask<T>(response.data.task_id, options);
    }
} 